import * as Joi from 'joi';
import _ from 'lodash';
import { CLIENT_STATE_KEYS, CLIENT_STATE_VERSIONS, DEFAULT_PERCENTAGE_FORMAT, } from '../../client-state-type';
import { AxisTypes, ChartType, } from '../../types/prism';
import { generateAxes } from '../chart-axis';
import { ClientStateV1Schema } from '../types/v1';
import { ClientStateV2Class } from '../types/v2';
import { isStackedChart } from './common-util';
const NULL_SUMMARY_MODE = 'None';
function getColumnProperties(clientState, vizModel) {
    const columnProperties = {};
    let enableLabelsOnMeasure = isStackedChart(vizModel.chartType)
        ? _.get(clientState, CLIENT_STATE_KEYS.detailedLabels)
        : _.get(clientState, CLIENT_STATE_KEYS.dataLabels);
    const isPieOrFunnelChart = vizModel.chartType === ChartType.Pie ||
        vizModel.chartType === ChartType.Funnel;
    const isBubbleOrScatterChart = vizModel.chartType === ChartType.Bubble ||
        vizModel.chartType === ChartType.Scatter;
    if (_.isUndefined(enableLabelsOnMeasure) && isPieOrFunnelChart) {
        enableLabelsOnMeasure = true;
    }
    const columns = vizModel.columns;
    columns.forEach(col => {
        columnProperties[col.id] = {
            dataLabels: enableLabelsOnMeasure,
        };
    });
    if (enableLabelsOnMeasure === true && isBubbleOrScatterChart) {
        const categoryColumnId = vizModel.axisConfig.categoryColumns;
        if (categoryColumnId) {
            _.each(columnProperties, (col, colId) => {
                if (colId !== categoryColumnId) {
                    delete col.dataLabels;
                }
            });
        }
    }
    return columnProperties;
}
const getAxisProperties = (clientState, vizModel) => {
    const yAxisColumns = vizModel.axisConfig.yAxisColumns;
    const yAxisRange = _.get(clientState, CLIENT_STATE_KEYS.yAxisRange, null);
    const isYAxisLinkingOptionNotAllowed = [
        ChartType.StackedColumn,
        ChartType.StackedBar,
        ChartType.StackedArea,
        ChartType.Candlestick,
    ].includes(vizModel.chartType);
    const yAxes = generateAxes(yAxisColumns, AxisTypes.Y, {
        isLinkColumns: _.get(clientState, CLIENT_STATE_KEYS.yAxisShared) ||
            isYAxisLinkingOptionNotAllowed,
        additionalProperties: {
            yAxisRange,
        },
    });
    const xAxisColumns = vizModel.axisConfig.xAxisColumns;
    const xAxis = generateAxes(xAxisColumns, AxisTypes.X);
    return [].concat(yAxes, xAxis);
};
function adaptAxisPropertiesToBeSaved(axes) {
    return axes.reduce((acc, value) => {
        acc[value.id] = value.properties;
        return acc;
    }, {});
}
function upgradeV1ToV2dot1(clientState, vizModel) {
    const columnProperties = getColumnProperties(clientState, vizModel);
    const axisProperties = adaptAxisPropertiesToBeSaved(getAxisProperties(clientState, vizModel));
    const updatedClientState = _.pick(clientState, [
        CLIENT_STATE_KEYS.SYSTEM_SERIES_COLORS,
        CLIENT_STATE_KEYS.SERIES_COLORS,
        CLIENT_STATE_KEYS.SYSTEM_MULTI_COLOR_SERIES_COLORS,
        CLIENT_STATE_KEYS.MULTI_COLOR_SERIES_COLORS,
        'custom_color_selector_array',
    ]);
    const chartProperties = {};
    const visibleSeriesNames = _.get(clientState, CLIENT_STATE_KEYS.visibleSeriesNames);
    if (visibleSeriesNames) {
        chartProperties[CLIENT_STATE_KEYS.visibleSeriesNames] = visibleSeriesNames;
    }
    const axisExtremes = _.get(clientState, CLIENT_STATE_KEYS.axisExtremes);
    if (axisExtremes) {
        chartProperties[CLIENT_STATE_KEYS.axisExtremes] = axisExtremes;
    }
    const gridLineConfig = _.get(clientState, CLIENT_STATE_KEYS.gridLineConfig);
    if (gridLineConfig) {
        chartProperties[CLIENT_STATE_KEYS.gridLines] = _.omit(gridLineConfig, [
            'x',
            'y',
        ]);
    }
    else {
        chartProperties[CLIENT_STATE_KEYS.gridLines] = {};
    }
    const isZoomed = _.get(clientState, CLIENT_STATE_KEYS.isZoomed);
    if (_.isBoolean(isZoomed)) {
        chartProperties[CLIENT_STATE_KEYS.isZoomed] = isZoomed;
    }
    const mapviewport = _.get(clientState, 'mapViewport');
    if (mapviewport) {
        chartProperties[CLIENT_STATE_KEYS.mapviewport] = mapviewport;
    }
    if (isStackedChart(vizModel.chartType) &&
        _.get(clientState, CLIENT_STATE_KEYS.dataLabels)) {
        chartProperties[CLIENT_STATE_KEYS.showStackedLabels] = true;
    }
    const showLinearRegressionLine = _.get(clientState, CLIENT_STATE_KEYS.showLinearRegressionLine);
    if (showLinearRegressionLine) {
        chartProperties[CLIENT_STATE_KEYS.showLinearRegressionLine] = showLinearRegressionLine;
    }
    const props = [
        CLIENT_STATE_KEYS.pivotState,
        CLIENT_STATE_KEYS.hidePivotSummaries,
        CLIENT_STATE_KEYS.summaryMode,
        CLIENT_STATE_KEYS.summaryFormat,
        CLIENT_STATE_KEYS.useFlatLayout,
        CLIENT_STATE_KEYS.isHeatmapOverlayed,
        CLIENT_STATE_KEYS.markersEnabled,
    ];
    const chartSpecificProperties = {};
    props.forEach(prop => {
        chartSpecificProperties[prop] = _.get(clientState, prop);
    });
    if (isStackedChart(vizModel.chartType) &&
        !!_.get(clientState, CLIENT_STATE_KEYS.IS_STACKED_AS_PERCENT)) {
        const stackedChartSpecificProperties = chartSpecificProperties;
        stackedChartSpecificProperties.isStackedAsPercent = true;
        stackedChartSpecificProperties.stackedAsPercentFormat = DEFAULT_PERCENTAGE_FORMAT;
    }
    const summaryMode = _.get(clientState, CLIENT_STATE_KEYS.summaryMode);
    if (vizModel.chartType === ChartType.PivotTable &&
        summaryMode !== NULL_SUMMARY_MODE &&
        !_.isNil(summaryMode)) {
        chartSpecificProperties[CLIENT_STATE_KEYS.summaryFormat] = DEFAULT_PERCENTAGE_FORMAT;
    }
    chartProperties[CLIENT_STATE_KEYS.chartSpecific] = chartSpecificProperties;
    const newClientState = {
        ...updatedClientState,
        axisProperties,
        columnProperties,
        chartProperties,
        [CLIENT_STATE_KEYS.version]: CLIENT_STATE_VERSIONS.V2dot1,
    };
    const clientStateV2 = new ClientStateV2Class();
    _.assign(clientStateV2, newClientState);
    return clientStateV2;
}
const upgraderFn = (clientState, vizModel) => {
    Joi.assert(clientState, ClientStateV1Schema);
    const clientStateVersion = _.get(clientState, CLIENT_STATE_KEYS.version);
    if (!_.isNil(clientStateVersion)) {
        throw new Error(`incompatible client state received  in chart upgrade path for v1 to v2.1 with version no:${clientStateVersion}`);
    }
    return upgradeV1ToV2dot1(clientState, vizModel);
};
const testHooks = {
    upgradeV1ToV2dot1,
    getColumnProperties,
    adaptAxisPropertiesToBeSaved,
};
export { upgraderFn, testHooks };
//# sourceMappingURL=v1-to-v2dot1.js.map